import { Suspense, useEffect } from "react";
import { Outlet, useParams } from "react-router";
import { useScreen } from "./use-screen";
import { TripStatusBadge } from "@/components/trips/trip-status-badge";
import { TripTabs } from "@/app/(app)/trips/[tripId]/trip-tabs";
import { TripBottomNav } from "@/components/navigation/trip-bottom-nav";
import { prefetchTripTabs } from "./prefetch-routes";
import { OfflineQueueFlusher } from "./offline-queue-flusher";
import type { Trip, Day } from "@/server/trips";

/**
 * 여행 화면들의 공통 껍데기 — 제목·기간·탭·하단 내비.
 *
 * Next의 `trips/[tripId]/layout.tsx`에 대응한다. 그쪽은 여기서 멤버십도
 * 확인했는데, SPA에서는 **각 API가 스스로 확인한다**(모든 라우트가
 * `assertMember`를 부른다). 여기서 또 막으면 왕복만 하나 늘고 보안은 그대로다.
 *
 * Next의 `TripPrefetch`(라우터 프리페치)에 대응하는 건 `prefetchTripTabs`다 —
 * 화면을 쪼갠 뒤로 탭 첫 진입에 모듈 내려받기가 붙어서(느린 휴대폰 실측 1.7초),
 * 브라우저가 한가할 때 미리 받아 둔다.
 */
export function TripLayout() {
  const { tripId } = useParams<{ tripId: string }>();
  useEffect(() => prefetchTripTabs(), []);
  // 머리글은 가벼운 조회로 채운다. 각 화면이 자기 데이터를 따로 받으므로
  // 여기서 큰 페이로드를 끌어오면 낭비다.
  const { data } = useScreen<{ trip: Trip; days: Day[] }>(
    tripId ? `/api/trips/${tripId}` : null
  );

  return (
    <div className="flex flex-col gap-6 pb-16 sm:pb-0">
      {data && (
        <div className="flex flex-col gap-1 print:hidden">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-2xl font-semibold tracking-tight">{data.trip.title}</h1>
            <TripStatusBadge startDate={data.trip.startDate} endDate={data.trip.endDate} />
          </div>
          <p className="text-muted-foreground text-sm">
            {data.trip.startDate} ~ {data.trip.endDate} · {data.days.length}일
            {data.trip.destination && ` · 📍 ${data.trip.destination}`}
          </p>
        </div>
      )}
      {tripId && <TripTabs tripId={tripId} />}
      {/* 밀린 지출은 어느 탭에 있든 올린다(offline-queue-flusher.tsx). */}
      {tripId && <OfflineQueueFlusher tripId={tripId} />}
      <Suspense fallback={<p className="p-6 text-sm text-muted-foreground">불러오는 중…</p>}>
        <Outlet />
      </Suspense>
      {tripId && <TripBottomNav tripId={tripId} />}
    </div>
  );
}
